// Sync Service - Processes queued offline events
// Routes each event type to the correct API call

import { EventTypes } from "./offlineQueue";
import * as api from "./supabase";
import { compressImage } from "./photoUpload";

// Photo upload URL for R2
const PHOTO_UPLOAD_URL =
  process.env.REACT_APP_PHOTO_UPLOAD_URL ||
  "https://time4subs-uploads.ryan-b00.workers.dev";

/**
 * Upload a blob to R2 storage
 * @param {Blob} blob - Image or audio blob
 * @param {string} key - Storage path
 * @returns {Promise<string>} Uploaded file key
 */
async function uploadToR2(blob, key) {
  const response = await fetch(
    `${PHOTO_UPLOAD_URL}?key=${encodeURIComponent(key)}`,
    {
      method: "PUT",
      body: blob,
      headers: { "Content-Type": blob.type || "application/octet-stream" },
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Upload failed");
  }

  const result = await response.json();
  return result.key;
}

/**
 * Compress a queued blob before upload, using the same 1920/0.8 policy as
 * the online capture paths. Falls back to the original blob if compression
 * fails (e.g. unsupported image format) so the upload can still proceed.
 * @param {Blob} blob - Original (uncompressed) blob from the offline queue
 * @param {string} name - Filename to give the intermediate File object
 * @returns {Promise<Blob>} Compressed blob, or the original on failure
 */
async function compressQueuedBlob(blob, name = "photo.jpg") {
  try {
    const file = new File([blob], name, { type: blob.type || "image/jpeg" });
    return await compressImage(file, 1920, 0.8);
  } catch (e) {
    console.warn("Compression failed, using original:", e);
    return blob;
  }
}

/**
 * Sync a PHOTO_ADD event
 * Uploads photo blob to R2, then saves metadata to database
 */
async function syncPhoto(event, blob) {
  const { payload, metadata } = event;

  if (!blob) {
    throw new Error("Photo blob not found");
  }

  const compressedBlob = await compressQueuedBlob(blob);

  // Generate storage key
  const date = new Date(metadata.timestamp).toISOString().split("T")[0];
  const key = `job-photos/${metadata.orgId}/${metadata.jobId}/${date}/${event.id}.jpg`;

  // Upload to R2
  const uploadedKey = await uploadToR2(compressedBlob, key);

  // Save metadata to database
  await api.saveJobPhoto({
    orgId: metadata.orgId,
    jobId: metadata.jobId,
    takenBy: metadata.userId,
    photoUrl: uploadedKey,
    category: payload.category || "progress",
    description: payload.description || "",
    latitude: metadata.gps?.lat,
    longitude: metadata.gps?.lng,
  });

  return { key: uploadedKey };
}

/**
 * Sync a VOICE_ADD event
 * Uploads audio blob, processes via voice worker
 * Note: Voice processing may need to be handled differently in offline mode
 */
async function syncVoice(event, blob) {
  const { payload, metadata } = event;

  if (!blob) {
    throw new Error("Voice blob not found");
  }

  // For now, we store the voice note as a status update with the transcript
  // Full voice processing would require the voice worker to be online

  // If we have a pre-transcribed result (from partial online processing)
  if (payload.transcript) {
    await api.postStatusUpdate({
      orgId: metadata.orgId,
      jobId: metadata.jobId,
      postedBy: metadata.userId,
      message: `[Voice Note] ${payload.transcript}`,
      updateType: "voice_note",
    });
    return { transcript: payload.transcript };
  }

  // Otherwise, upload the audio and mark for later processing
  const date = new Date(metadata.timestamp).toISOString().split("T")[0];
  const ext = blob.type.includes("webm") ? "webm" : "m4a";
  const key = `voice-notes/${metadata.orgId}/${metadata.jobId}/${date}/${event.id}.${ext}`;

  const uploadedKey = await uploadToR2(blob, key);

  // Save as pending voice note
  await api.postStatusUpdate({
    orgId: metadata.orgId,
    jobId: metadata.jobId,
    postedBy: metadata.userId,
    message: `[Voice Note - Processing Pending] Audio saved: ${uploadedKey}`,
    updateType: "voice_note",
  });

  return { key: uploadedKey };
}

/**
 * Sync a STATUS_ADD event
 */
async function syncStatus(event) {
  const { payload, metadata } = event;

  await api.postStatusUpdate({
    orgId: metadata.orgId,
    jobId: metadata.jobId,
    postedBy: metadata.userId,
    message: payload.message,
    updateType: payload.updateType || "note",
    photoUrl: payload.photoUrl,
  });

  return { success: true };
}

/**
 * Sync a TIME_ALLOCATION_ADD event
 */
async function syncTimeAllocation(event) {
  const { payload, metadata } = event;

  // payload should contain: timeEntryId, allocations array
  if (!payload.timeEntryId) {
    throw new Error("Missing timeEntryId for allocation");
  }

  await api.saveAllocations(
    payload.timeEntryId,
    payload.allocations,
    metadata.userId,
  );

  return { success: true };
}

/**
 * Sync a CLOCK_IN event
 */
async function syncClockIn(event, blob) {
  const { payload, metadata } = event;

  let photoUrl = null;

  // Upload clock-in photo if provided
  if (blob) {
    const compressedBlob = await compressQueuedBlob(blob);
    const date = new Date(metadata.timestamp).toISOString().split("T")[0];
    const key = `clock-photos/${metadata.orgId}/clock-in/${date}/${payload.employeeId}/${event.id}.jpg`;
    photoUrl = await uploadToR2(compressedBlob, key);
  }

  const timeEntry = await api.clockIn({
    orgId: metadata.orgId,
    employeeId: payload.employeeId,
    jobId: metadata.jobId,
    jobTimezone: payload.jobTimezone || "America/Denver",
    latitude: metadata.gps?.lat,
    longitude: metadata.gps?.lng,
    photoUrl,
    enteredBy: metadata.userId,
  });

  return { timeEntryId: timeEntry.id };
}

/**
 * Sync a CLOCK_OUT event
 */
async function syncClockOut(event, blob) {
  const { payload, metadata } = event;

  if (!payload.timeEntryId) {
    throw new Error("Missing timeEntryId for clock out");
  }
  if (!metadata.timestamp || !Number.isFinite(Date.parse(metadata.timestamp))) {
    throw new Error("Missing recorded clock-out time. Ask the office to review this queued punch.");
  }

  let photoUrl = null;

  // Upload clock-out photo if provided
  if (blob) {
    const compressedBlob = await compressQueuedBlob(blob);
    const date = new Date(metadata.timestamp).toISOString().split("T")[0];
    const key = `clock-photos/${metadata.orgId}/clock-out/${date}/${payload.employeeId}/${event.id}.jpg`;
    photoUrl = await uploadToR2(compressedBlob, key);
  }

  await api.clockOut(payload.timeEntryId, {
    clockOut: metadata.timestamp,
    latitude: metadata.gps?.lat,
    longitude: metadata.gps?.lng,
    photoUrl,
  });

  return { success: true };
}

/**
 * Sync a PRODUCTION_ADD event
 */
async function syncProduction(event) {
  const { payload, metadata } = event;

  await api.addProduction({
    orgId: metadata.orgId,
    jobId: metadata.jobId,
    costCodeId: payload.costCodeId,
    quantity: payload.quantity,
    enteredBy: metadata.userId,
    notes: payload.notes,
  });

  return { success: true };
}

/**
 * Main sync function - routes event to correct handler
 * @param {object} event - Queued event from IndexedDB
 * @param {Blob|null} blob - Associated blob data if any
 * @returns {Promise<object>} Sync result
 */
export async function syncEvent(event, blob = null) {
  switch (event.type) {
    case EventTypes.PHOTO_ADD:
      return syncPhoto(event, blob);

    case EventTypes.VOICE_ADD:
      return syncVoice(event, blob);

    case EventTypes.STATUS_ADD:
      return syncStatus(event);

    case EventTypes.TIME_ALLOCATION_ADD:
      return syncTimeAllocation(event);

    case EventTypes.CLOCK_IN:
      return syncClockIn(event, blob);

    case EventTypes.CLOCK_OUT:
      return syncClockOut(event, blob);

    case EventTypes.PRODUCTION_ADD:
      return syncProduction(event);

    default:
      throw new Error(`Unknown event type: ${event.type}`);
  }
}

export default { syncEvent };
